import React from 'react';
import { Typography, Button, Divider, Space, message, Card, Tag } from 'antd';
import { CopyOutlined, FileTextOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const DOCS_BASE_URL = 'https://exellius.com/api-documentation';
const API_HOST = 'https://api.exellius.com';

// Matches the sidebar nav structure of the documentation page
const endpointSections = [
  {
    title: 'Search',
    items: [
      {
        id: 'search-people-emails',
        label: 'Search People for Emails',
        method: 'POST',
        path: '/api/v1/people/search/email',
      },
      {
        id: 'search-people-phones',
        label: 'Search People for Phones',
        method: 'POST',
        path: '/api/v1/people/search/phone',
      },
      {
        id: 'search-people',
        label: 'Search People',
        method: 'POST',
        path: '/api/v1/people/search',
      },
    ],
  },
  {
    title: 'Email',
    items: [
      {
        id: 'email-finder',
        label: 'Find Person Email',
        method: 'GET',
        path: '/api/v1/people/find/email',
      },
      {
        id: 'domain-finder',
        label: 'Find Emails by Company Domain',
        method: 'GET',
        path: '/api/v1/people/search/domain/email',
      },
      {
        id: 'linkedin-email-finder',
        label: 'Find Email by LinkedIn URL',
        method: 'GET',
        path: '/api/v1/people/search/linkedin/email',
      },
      {
        id: 'email-verifier',
        label: 'Verify Email',
        method: 'GET',
        path: '/api/v1/people/verify/email',
      },
    ],
  },
  {
    title: 'Phone',
    items: [
      {
        id: 'phone-finder',
        label: 'Find Person Phone',
        method: 'GET',
        path: '/api/v1/people/find/phone',
      },
      {
        id: 'domain-phone-search',
        label: 'Find Phones by Company Domain',
        method: 'GET',
        path: '/api/v1/people/search/domain/phone',
      },
      {
        id: 'linkedin-phone',
        label: 'Find Phone by LinkedIn URL',
        method: 'GET',
        path: '/api/v1/people/search/linkedin/phone',
      },
    ],
  },
  {
    title: 'Enrichment',
    items: [
      {
        id: 'email-enrichment',
        label: 'Enrich Person with Email',
        method: 'GET',
        path: '/api/v1/people/enrich/email',
      },
      {
        id: 'linkedin-enrichment',
        label: 'Enrich Person by LinkedIn URL',
        method: 'GET',
        path: '/api/v1/people/enrich/linkedin',
      },
      {
        id: 'phone-enrichment',
        label: 'Enrich Person by Phone',
        method: 'GET',
        path: '/api/v1/people/enrich/phone',
      },
      {
        id: 'company-enrichment',
        label: 'Enrich Company',
        method: 'GET',
        path: '/api/v1/companies/enrich',
      },
    ],
  },
];

// Masks a token the same way Hunter's dashboard does: dots + last 4 characters
const maskToken = (token) => {
  if (!token) return '••••••••••••••••••••••••••••<api_key>';
  const last4 = token.slice(-4);
  return `${'•'.repeat(Math.max(token.length - 4, 24))}${last4}`;
};

export default function ApiEndpointsList({ apiKeys = [] }) {
  const activeToken =
    apiKeys.find((key) => key.is_active)?.token || apiKeys[0]?.token || null;
  const maskedToken = maskToken(activeToken);

  const handleCopy = async (item) => {
    const curl = `curl -X ${item.method} "${API_HOST}${item.path}" \\\n  -H "Authorization: Bearer ${activeToken || '<api_key>'}"`;

    try {
      await navigator.clipboard.writeText(curl);
      message.success('Request copied');
    } catch (error) {
      message.error('Unable to copy request');
    }
  };

  const handleOpenDocs = (id) => {
    window.open(`${DOCS_BASE_URL}#${id}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="api-endpoints-list" style={{ marginTop: 48 }}>
      {endpointSections.map((section) => (
        <div key={section.title} style={{ marginBottom: 48 }}>
          {/* Section Header */}
          <div style={{ marginBottom: 20 }}>
            <Title level={3} style={{ marginBottom: 6 }}>
              {section.title}
            </Title>
            <Text type="secondary">
              {section.items.length} endpoint{section.items.length > 1 ? 's' : ''}
            </Text>
          </div>

          <Space direction="vertical" size={20} style={{ width: '100%' }}>
            {section.items.map((item) => (
              <Card
                key={item.id}
                hoverable
                bordered={false}
                style={{
                  borderRadius: 14,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                }}
                bodyStyle={{ padding: 20 }}
              >
                {/* Top */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 16,
                    gap: 20,
                  }}
                >
                  <div>
                    <Text strong style={{ fontSize: 16 }}>
                      {item.label}
                    </Text>

                    <div style={{ marginTop: 6 }}>
                      <Tag
                        color={
                          item.method === 'GET'
                            ? 'green'
                            : item.method === 'POST'
                            ? 'blue'
                            : item.method === 'DELETE'
                            ? 'red'
                            : 'orange'
                        }
                        style={{
                          fontWeight: 600,
                          borderRadius: 20,
                          padding: '2px 10px',
                        }}
                      >
                        {item.method}
                      </Tag>
                    </div>
                  </div>

                  <Space>
                    <Button
                      icon={<CopyOutlined />}
                      onClick={() => handleCopy(item)}
                    >
                      Copy
                    </Button>

                    <Button
                      type="primary"
                      ghost
                      icon={<FileTextOutlined />}
                      onClick={() => handleOpenDocs(item.id)}
                    >
                      View Docs
                    </Button>
                  </Space>
                </div>

                {/* Endpoint */}
                <div
                  style={{
                    background: '#0f172a',
                    borderRadius: 12,
                    padding: 18,
                    overflowX: 'auto',
                    border: '1px solid #1e293b',
                  }}
                >
                  <div
                    style={{
                      fontFamily:
                        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                      fontSize: 14,
                      color: '#E2E8F0',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <span
                      style={{
                        color:
                          item.method === 'GET'
                            ? '#22c55e'
                            : item.method === 'POST'
                            ? '#3b82f6'
                            : item.method === 'DELETE'
                            ? '#ef4444'
                            : '#f59e0b',
                        fontWeight: 700,
                        marginRight: 10,
                      }}
                    >
                      {item.method}
                    </span>

                    <span style={{ color: '#94A3B8' }}>{API_HOST}</span>

                    <span style={{ color: '#38BDF8' }}>{item.path}</span>
                  </div>

                  <Divider
                    style={{
                      borderColor: '#334155',
                      margin: '14px 0',
                    }}
                  />

                  <div
                    style={{
                      fontFamily:
                        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                      fontSize: 13,
                      color: '#CBD5E1',
                    }}
                  >
                    <span style={{ color: '#64748B' }}>Authorization:</span>{' '}
                    Bearer{' '}
                    <span style={{ color: '#60A5FA' }}>{maskedToken}</span>
                  </div>
                </div>
              </Card>
            ))}
          </Space>
        </div>
      ))}
    </div>
  );
}